const TITLECASE_PHRASE_REGEX = /\b([A-Z][A-Za-z0-9&._-]*(?:\s+[A-Z][A-Za-z0-9&._-]*){1,4})\b/g;
const ACRONYM_TOKEN_REGEX = /\b([A-Z]{2,}(?:-[A-Z0-9]{2,})?)\b/g;
const GROUNDING_SINGLE_TOKEN_STOPWORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'be', 'by', 'for', 'from', 'in', 'is', 'it', 'my', 'of', 'on', 'or',
  'our', 'that', 'the', 'this', 'to', 'was', 'we', 'with', 'you', 'your'
]);

const normalizeAnchorValue = (value = '') => {
  return String(value || '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^["'`\s]+|["'`\s]+$/g, '')
    .replace(/^[,.;:!?()[\]{}]+|[,.;:!?()[\]{}]+$/g, '')
    .trim()
    .toLowerCase();
};

const pushAnchorsFromRegex = ({ regex, text = '', minLength = 3, anchors = new Set(), accept }) => {
  regex.lastIndex = 0;
  let match = regex.exec(text);
  while (match) {
    const normalized = normalizeAnchorValue(match[1] || '');
    if (
      normalized
      && normalized.length >= minLength
      && (typeof accept !== 'function' || accept(normalized))
    ) {
      anchors.add(normalized);
    }
    match = regex.exec(text);
  }
  regex.lastIndex = 0;
};

const extractGroundingAnchors = (value = '') => {
  const text = String(value || '');
  if (!text) return [];
  const anchors = new Set();

  pushAnchorsFromRegex({ regex: /`([^`]{2,80})`/g, text, anchors });
  pushAnchorsFromRegex({ regex: /"([^"]{3,80})"/g, text, anchors });
  pushAnchorsFromRegex({ regex: /'([^']{3,80})'/g, text, anchors });
  pushAnchorsFromRegex({ regex: TITLECASE_PHRASE_REGEX, text, anchors });
  pushAnchorsFromRegex({
    regex: ACRONYM_TOKEN_REGEX,
    text,
    anchors,
    accept: (token) => !GROUNDING_SINGLE_TOKEN_STOPWORDS.has(token)
  });

  return Array.from(anchors);
};

const tokenizeAnchor = (value = '') => {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, ' ')
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 2 && !GROUNDING_SINGLE_TOKEN_STOPWORDS.has(token));
};

const anchorsLikelyMatch = ({ responseAnchor = '', evidenceAnchor = '' } = {}) => {
  const responseNormalized = normalizeAnchorValue(responseAnchor);
  const evidenceNormalized = normalizeAnchorValue(evidenceAnchor);
  if (!responseNormalized || !evidenceNormalized) return false;
  if (responseNormalized === evidenceNormalized) return true;

  if (responseNormalized.includes(evidenceNormalized) || evidenceNormalized.includes(responseNormalized)) {
    const minLength = Math.min(responseNormalized.length, evidenceNormalized.length);
    if (minLength >= 4) return true;
  }

  const responseTokens = tokenizeAnchor(responseNormalized);
  const evidenceTokens = tokenizeAnchor(evidenceNormalized);
  if (responseTokens.length === 0 || evidenceTokens.length === 0) return false;

  const responseSet = new Set(responseTokens);
  const evidenceSet = new Set(evidenceTokens);
  const responseContainsEvidence = evidenceTokens.every((token) => responseSet.has(token));
  const evidenceContainsResponse = responseTokens.every((token) => evidenceSet.has(token));

  if ((responseContainsEvidence && evidenceTokens.length >= 2) || (evidenceContainsResponse && responseTokens.length >= 2)) {
    return true;
  }

  return false;
};

export const collectGroundingEvidenceAnchors = (contextItems = []) => {
  const anchors = new Set();
  for (const item of Array.isArray(contextItems) ? contextItems : []) {
    const values = extractGroundingAnchors(item?.text || '');
    values.forEach((value) => anchors.add(value));
  }
  return Array.from(anchors);
};

export const validateGroundingAnchors = ({ responseText = '', evidenceAnchors = [] } = {}) => {
  const responseAnchors = extractGroundingAnchors(responseText);
  if (responseAnchors.length === 0) {
    return {
      isGrounded: true,
      responseAnchors: [],
      ungroundedAnchors: []
    };
  }

  const evidenceList = Array.isArray(evidenceAnchors)
    ? evidenceAnchors.map((value) => normalizeAnchorValue(value)).filter(Boolean)
    : [];
  if (evidenceList.length === 0) {
    return {
      isGrounded: false,
      responseAnchors,
      ungroundedAnchors: responseAnchors
    };
  }

  const ungroundedAnchors = responseAnchors.filter((name) => {
    return !evidenceList.some((evidenceName) => anchorsLikelyMatch({
      responseAnchor: name,
      evidenceAnchor: evidenceName
    }));
  });
  return {
    isGrounded: ungroundedAnchors.length === 0,
    responseAnchors,
    ungroundedAnchors
  };
};

export const buildGroundingRetryMessage = ({ evidenceAnchors = [] } = {}) => {
  const anchorHint = Array.isArray(evidenceAnchors) && evidenceAnchors.length > 0
    ? `Use only named facts explicitly supported by context: ${evidenceAnchors.join(', ')}.`
    : 'Use only named facts explicitly supported by the provided context.';

  return [
    'Grounding policy (strict):',
    '- Do not introduce names, identifiers, or quoted terms unless present in context.',
    '- If evidence is insufficient, state uncertainty explicitly.',
    anchorHint
  ].join(' ');
};

export const buildGroundingUncertaintyText = ({ ungroundedAnchors = [] } = {}) => {
  const list = Array.isArray(ungroundedAnchors) ? ungroundedAnchors.filter(Boolean) : [];
  const rendered = list.length > 0 ? ` (${list.join(', ')})` : '';
  return `I do not have enough grounded evidence to confirm those named details${rendered}. Please provide a source document or clarify the specific references you want used.`;
};

// Validates grounding anchors and optionally retries with stricter grounding instructions.
export const executeGroundingStage = async ({
  enabled = false,
  rawText = '',
  stream = false,
  messages = [],
  evidenceAnchors = [],
  response = null,
  responseText = '',
  executeRequest,
  normalizeResponseText,
  extractResponseText,
  validateGroundingAnchors: validateGroundingAnchorsFn,
  buildGroundingRetryMessage: buildGroundingRetryMessageFn,
  buildGroundingUncertaintyText: buildGroundingUncertaintyTextFn
} = {}) => {
  let groundingRetryUsed = false;
  let groundingStatus = enabled ? 'grounding-not-required' : 'grounding-skipped';
  let groundingReasonCodes = [];
  let groundingUngroundedEntities = [];
  const preserveStreamedGroundingText = Boolean(stream);

  let groundedRawText = String(rawText || '');
  if (!enabled || typeof validateGroundingAnchorsFn !== 'function') {
    return {
      response,
      responseText,
      groundedRawText,
      groundingStatus,
      groundingRetryUsed,
      groundingReasonCodes,
      groundingUngroundedEntities
    };
  }

  let groundingCheck = validateGroundingAnchorsFn({
    responseText: groundedRawText,
    evidenceAnchors
  });

  if (!groundingCheck.isGrounded) {
    groundingReasonCodes.push('anchor_ungrounded');
    groundingUngroundedEntities = Array.isArray(groundingCheck.ungroundedAnchors)
      ? groundingCheck.ungroundedAnchors
      : [];

    if (preserveStreamedGroundingText) {
      groundingStatus = 'grounding-warning-stream-preserved';
    } else {
      groundingRetryUsed = true;
      groundingReasonCodes.push('grounding_retry_used');
      const retryMessages = [
        ...(Array.isArray(messages) ? messages : []),
        {
          role: 'system',
          content: typeof buildGroundingRetryMessageFn === 'function'
            ? buildGroundingRetryMessageFn({ evidenceAnchors })
            : ''
        }
      ];

      try {
        const retryResult = typeof executeRequest === 'function'
          ? await executeRequest(retryMessages)
          : null;
        response = retryResult?.response || response;
        responseText = retryResult?.responseText || responseText;
        groundedRawText = Boolean(stream)
          ? (typeof normalizeResponseText === 'function' ? normalizeResponseText(responseText) : String(responseText || ''))
          : (typeof normalizeResponseText === 'function'
            ? normalizeResponseText(typeof extractResponseText === 'function' ? extractResponseText(response) : '')
            : String(typeof extractResponseText === 'function' ? extractResponseText(response) : ''));

        groundingCheck = validateGroundingAnchorsFn({
          responseText: groundedRawText,
          evidenceAnchors
        });
      } catch (error) {
        groundingCheck = {
          isGrounded: false,
          ungroundedAnchors: groundingCheck.ungroundedAnchors || []
        };
      }

      if (!groundingCheck.isGrounded) {
        groundingStatus = 'grounding-uncertainty-returned';
        groundingReasonCodes.push('grounding_uncertainty_returned');
        groundingUngroundedEntities = Array.isArray(groundingCheck.ungroundedAnchors)
          ? groundingCheck.ungroundedAnchors
          : [];
        groundedRawText = typeof buildGroundingUncertaintyTextFn === 'function'
          ? buildGroundingUncertaintyTextFn({ ungroundedAnchors: groundingUngroundedEntities })
          : groundedRawText;
      } else {
        groundingStatus = 'grounded-after-retry';
        groundingUngroundedEntities = [];
      }
    }
  } else {
    groundingStatus = 'grounded';
    groundingUngroundedEntities = [];
  }

  return {
    response,
    responseText,
    groundedRawText,
    groundingStatus,
    groundingRetryUsed,
    groundingReasonCodes,
    groundingUngroundedEntities
  };
};
